import { useState } from 'react';
import { useQuery, useMutation } from '@tanstack/react-query';
import {
  CloudIcon,
  BeakerIcon,
  CalendarDaysIcon,
  ArrowTrendingUpIcon,
  ExclamationTriangleIcon,
  CheckCircleIcon,
  InformationCircleIcon,
} from '@heroicons/react/24/outline';
import {
  AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend,
  Bar, ComposedChart, Line,
} from 'recharts';
import { format } from 'date-fns';
import { farmApi, irrigationApi, weatherApi, IrrigationSchedule, IrrigationDay } from '../lib/api';
import toast from 'react-hot-toast';

function StressBadge({ level, color }: { level: string; color: string }) {
  const icons: Record<string, JSX.Element> = {
    none: <CheckCircleIcon className="h-4 w-4" />,
    mild: <InformationCircleIcon className="h-4 w-4" />,
    moderate: <ExclamationTriangleIcon className="h-4 w-4" />,
    severe: <ExclamationTriangleIcon className="h-4 w-4" />,
  };

  return (
    <span
      className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium text-white"
      style={{ backgroundColor: color }}
    >
      {icons[level]}
      <span className="ml-1 capitalize">{level}</span>
    </span>
  );
}

function StatCard({ title, value, unit, icon: Icon, color, subtitle }: {
  title: string;
  value: number | string;
  unit?: string;
  icon: React.ElementType;
  color: string;
  subtitle?: string;
}) {
  return (
    <div className="bg-white rounded-xl shadow-sm border p-6">
      <div className="flex items-center justify-between">
        <div>
          <p className="text-sm text-gray-500">{title}</p>
          <p className="mt-1 text-2xl font-bold text-gray-900">
            {typeof value === 'number' ? value.toFixed(1) : value}
            {unit && <span className="text-sm text-gray-500 ml-1">{unit}</span>}
          </p>
          {subtitle && <p className="text-xs text-gray-500 mt-1">{subtitle}</p>}
        </div>
        <div className={`p-3 rounded-lg ${color}`}>
          <Icon className="h-6 w-6 text-white" />
        </div>
      </div>
    </div>
  );
}

function ScheduleDay({ day }: { day: IrrigationDay }) {
  return (
    <div className={`p-4 rounded-lg border ${day.irrigationRecommended ? 'bg-blue-50 border-blue-200' : 'bg-gray-50'}`}>
      <div className="flex items-center justify-between mb-2">
        <span className="font-medium text-gray-900">
          {format(new Date(day.date), 'EEE, MMM d')}
        </span>
        <StressBadge level={day.stressLevel} color={day.stressColor} />
      </div>
      <div className="grid grid-cols-2 gap-2 text-sm">
        <div>
          <p className="text-gray-500">ET0</p>
          <p className="font-medium">{day.et0.toFixed(2)} mm</p>
        </div>
        <div>
          <p className="text-gray-500">ETc</p>
          <p className="font-medium">{day.etc.toFixed(2)} mm</p>
        </div>
        <div>
          <p className="text-gray-500">Rain</p>
          <p className="font-medium">{day.precipitation.toFixed(1)} mm</p>
        </div>
        <div>
          <p className="text-gray-500">Irrigation</p>
          <p className={`font-medium ${day.irrigationRecommended ? 'text-blue-600' : ''}`}>
            {day.irrigationNeeded.toFixed(1)} mm
          </p>
        </div>
      </div>
      {day.irrigationRecommended && (
        <div className="mt-2 flex items-center text-blue-700 text-sm">
          <CloudIcon className="h-4 w-4 mr-1" />
          Irrigation recommended
        </div>
      )}
    </div>
  );
}

export default function Irrigation() {
  const [selectedFarm, setSelectedFarm] = useState<string>('');
  const [soilType, setSoilType] = useState<string>('loam');
  const [daysAfterPlanting, setDaysAfterPlanting] = useState<number>(45);
  const [schedule, setSchedule] = useState<IrrigationSchedule | null>(null);

  const { data: farms = [], isLoading: farmsLoading } = useQuery({
    queryKey: ['farms'],
    queryFn: farmApi.list,
  });

  const { data: soilData } = useQuery({
    queryKey: ['irrigation-soils'],
    queryFn: irrigationApi.getSupportedSoils,
  });

  const { data: systemsData } = useQuery({
    queryKey: ['irrigation-systems'],
    queryFn: irrigationApi.getSupportedSystems,
  });

  const scheduleMutation = useMutation({
    mutationFn: irrigationApi.generateSchedule,
    onSuccess: (response) => {
      setSchedule(response.data);
      toast.success('Irrigation schedule generated!');
    },
    onError: () => {
      toast.error('Failed to generate schedule');
    },
  });

  const handleGenerate = async () => {
    const farm = farms.find(f => f.id === selectedFarm);
    if (!farm) {
      toast.error('Please select a farm');
      return;
    }

    try {
      // Fetch real weather forecast from API
      const weatherResult = await weatherApi.getForecast(selectedFarm, 7);
      const weatherData = weatherResult.data || weatherResult.forecast || [];

      if (!weatherData || weatherData.length === 0) {
        toast.error('No weather data available for this farm');
        return;
      }

      // Transform weather data to the format expected by irrigation schedule
      const weatherForecast = weatherData.map((day: { date: string; temperatureMin: number; temperatureMax: number; humidity: number; windSpeed?: number; precipitation?: number }) => ({
        date: day.date,
        temperatureMax: day.temperatureMax,
        temperatureMin: day.temperatureMin,
        humidity: day.humidity,
        windSpeed: day.windSpeed || 2,
        precipitation: day.precipitation || 0,
      }));

      // Get centroid of farm for latitude
      const lat = farm.boundary?.reduce((sum, c) => sum + c.lat, 0) / (farm.boundary?.length || 1) || -1.3;

      scheduleMutation.mutate({
        farmId: selectedFarm,
        cropType: farm.cropType || 'corn',
        soilType,
        daysAfterPlanting,
        location: { latitude: lat, elevation: 1500 },
        weatherForecast,
      });
    } catch {
      toast.error('Failed to fetch weather data');
    }
  };

  const chartData = schedule?.schedule.map(day => ({
    date: format(new Date(day.date), 'MMM d'),
    ET0: day.et0,
    ETc: day.etc,
    Rain: day.precipitation,
    Irrigation: day.irrigationNeeded,
    SoilMoisture: day.soilMoistureEnd,
  })) || [];

  if (farmsLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-green-600" />
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-bold text-gray-900">Irrigation Management</h1>
        <p className="text-gray-500">Smart irrigation scheduling based on ET calculations</p>
      </div>

      {/* Configuration */}
      <div className="bg-white rounded-xl shadow-sm border p-6">
        <h2 className="text-lg font-semibold text-gray-900 mb-4">Generate Irrigation Schedule</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Select Farm
            </label>
            <select
              value={selectedFarm}
              onChange={(e) => setSelectedFarm(e.target.value)}
              className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-green-500"
            >
              <option value="">Choose a farm...</option>
              {farms.map(farm => (
                <option key={farm.id} value={farm.id}>
                  {farm.name} ({farm.cropType || 'Unknown'})
                </option>
              ))}
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Soil Type
            </label>
            <select
              value={soilType}
              onChange={(e) => setSoilType(e.target.value)}
              className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-green-500"
            >
              {(soilData?.data?.soilTypes || ['sand', 'loam', 'clay', 'sandy_loam', 'clay_loam']).map(soil => (
                <option key={soil} value={soil}>
                  {soil.replace('_', ' ').replace(/\b\w/g, l => l.toUpperCase())}
                </option>
              ))}
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Days After Planting
            </label>
            <input
              type="number"
              value={daysAfterPlanting}
              onChange={(e) => setDaysAfterPlanting(parseInt(e.target.value) || 0)}
              min={0}
              max={365}
              className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-green-500"
            />
          </div>

          <div className="flex items-end">
            <button
              onClick={handleGenerate}
              disabled={!selectedFarm || scheduleMutation.isPending}
              className="w-full px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50 flex items-center justify-center"
            >
              {scheduleMutation.isPending ? (
                <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white" />
              ) : (
                <>
                  <CalendarDaysIcon className="h-5 w-5 mr-2" />
                  Generate Schedule
                </>
              )}
            </button>
          </div>
        </div>
      </div>

      {/* Supported Systems Info */}
      {systemsData?.data?.systems && (
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
          <div className="flex items-start space-x-3">
            <InformationCircleIcon className="h-5 w-5 text-blue-600 flex-shrink-0 mt-0.5" />
            <div>
              <p className="text-sm font-medium text-blue-900">Supported Irrigation Systems</p>
              <p className="text-sm text-blue-700">
                {systemsData.data.systems.join(', ')}
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Schedule Results */}
      {schedule && (
        <div className="space-y-6">
          {/* Summary Stats */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4">
            <StatCard
              title="Total Irrigation"
              value={schedule.summary.totalIrrigationNeeded}
              unit="mm"
              icon={CloudIcon}
              color="bg-blue-600"
            />
            <StatCard
              title="Total Precipitation"
              value={schedule.summary.totalPrecipitation}
              unit="mm"
              icon={CloudIcon}
              color="bg-cyan-600"
            />
            <StatCard
              title="Days Needing Irrigation"
              value={schedule.summary.daysRequiringIrrigation}
              unit="days"
              icon={CalendarDaysIcon}
              color="bg-orange-600"
            />
            <StatCard
              title="Average ET0"
              value={schedule.summary.averageET0}
              unit="mm/day"
              icon={ArrowTrendingUpIcon}
              color="bg-green-600"
            />
            <StatCard
              title="Average ETc"
              value={schedule.summary.averageETc}
              unit="mm/day"
              icon={BeakerIcon}
              color="bg-purple-600"
            />
          </div>

          {/* Water Balance Chart */}
          <div className="bg-white rounded-xl shadow-sm border p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Water Balance (7-Day Forecast)</h3>
            <div className="h-80">
              <ResponsiveContainer width="100%" height="100%">
                <ComposedChart data={chartData}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                  <XAxis dataKey="date" tick={{ fontSize: 12 }} />
                  <YAxis yAxisId="left" tick={{ fontSize: 12 }} />
                  <YAxis yAxisId="right" orientation="right" tick={{ fontSize: 12 }} domain={[0, 100]} />
                  <Tooltip />
                  <Legend />
                  <Bar yAxisId="left" dataKey="Rain" name="Rain (mm)" fill="#06b6d4" radius={[4, 4, 0, 0]} />
                  <Bar yAxisId="left" dataKey="Irrigation" name="Irrigation (mm)" fill="#3b82f6" radius={[4, 4, 0, 0]} />
                  <Line yAxisId="left" type="monotone" dataKey="ETc" name="ETc (mm)" stroke="#ef4444" strokeWidth={2} dot={{ fill: '#ef4444' }} />
                  <Line yAxisId="right" type="monotone" dataKey="SoilMoisture" name="Soil Moisture (%)" stroke="#22c55e" strokeWidth={2} dot={{ fill: '#22c55e' }} />
                </ComposedChart>
              </ResponsiveContainer>
            </div>
          </div>

          {/* ET Chart */}
          <div className="bg-white rounded-xl shadow-sm border p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Evapotranspiration Trend</h3>
            <div className="h-64">
              <ResponsiveContainer width="100%" height="100%">
                <AreaChart data={chartData}>
                  <defs>
                    <linearGradient id="et0Gradient" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor="#f59e0b" stopOpacity={0.3} />
                      <stop offset="95%" stopColor="#f59e0b" stopOpacity={0} />
                    </linearGradient>
                    <linearGradient id="etcGradient" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor="#10b981" stopOpacity={0.3} />
                      <stop offset="95%" stopColor="#10b981" stopOpacity={0} />
                    </linearGradient>
                  </defs>
                  <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                  <XAxis dataKey="date" tick={{ fontSize: 12 }} />
                  <YAxis tick={{ fontSize: 12 }} />
                  <Tooltip />
                  <Legend />
                  <Area
                    type="monotone"
                    dataKey="ET0"
                    name="ET0 (Reference)"
                    stroke="#f59e0b"
                    fill="url(#et0Gradient)"
                    strokeWidth={2}
                  />
                  <Area
                    type="monotone"
                    dataKey="ETc"
                    name="ETc (Crop)"
                    stroke="#10b981"
                    fill="url(#etcGradient)"
                    strokeWidth={2}
                  />
                </AreaChart>
              </ResponsiveContainer>
            </div>
          </div>

          {/* Daily Schedule */}
          <div className="bg-white rounded-xl shadow-sm border p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Daily Irrigation Schedule</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
              {schedule.schedule.map(day => (
                <ScheduleDay key={day.date} day={day} />
              ))}
            </div>
          </div>

          {/* Metadata */}
          <div className="bg-gray-50 rounded-lg p-4 text-sm text-gray-600">
            <p>
              <strong>Farm:</strong> {schedule.farmId} |{' '}
              <strong>Crop:</strong> {schedule.cropType} |{' '}
              <strong>Soil:</strong> {schedule.soilType} |{' '}
              <strong>Generated:</strong> {new Date(schedule.generatedAt).toLocaleString()}
            </p>
          </div>
        </div>
      )}

      {/* Empty State */}
      {!schedule && (
        <div className="bg-white rounded-xl shadow-sm border p-12 text-center">
          <CloudIcon className="h-16 w-16 mx-auto text-gray-300 mb-4" />
          <h3 className="text-lg font-medium text-gray-900 mb-2">No Schedule Generated</h3>
          <p className="text-gray-500 max-w-md mx-auto">
            Select a farm and configure parameters above to generate a smart irrigation schedule
            based on FAO Penman-Monteith evapotranspiration calculations.
          </p>
        </div>
      )}
    </div>
  );
}
